import { useMemo, useState } from 'react';
import { useNavigate, useParams, useSearchParams } from 'react-router-dom';
import { Button, Sheet } from '@dreamtree-org/twreact-ui';
import { EmptyState } from '@/components';
import { SORTS, filtersToParams, parseFilters } from '../lib/filters';
import { effectiveSort, templateOf, withSortParam } from '../lib/design';
import { useStorefront } from '../StorefrontContext';
import Region, { useWide } from '../design/Region';
import { ListingCtx, LISTING_SLOT_COMPONENTS, buildListing, pageSizeOf } from '../design/listing';
import { useCatalogSearch } from '../lib/useCatalogSearch';

/**
 * WEB-02 search and filters, for every product type (B11-P3): /shop lists all
 * stock with the common filters, /shop/<type slug> one type with filters
 * generated from its attributes. The URL is the state: every filter, the sort
 * and the page live in the query string, so results are shareable and Back works.
 * The server filters, sorts, pages and counts in the query (Listing.search,
 * MERCH-86): attribute filters, the store's own filterable fields and facets included.
 *
 * The layout is the owner's listing template (D64): its blocks in order, with
 * columns, page size, default sort, filter style and paging from its settings.
 */
export default function Search() {
  const sf = useStorefront();
  const navigate = useNavigate();
  const { type: slug } = useParams();
  const [params, setParams] = useSearchParams();
  const [sheet, setSheet] = useState(false);
  const wide = useWide();
  const tpl = templateOf(sf.doc, 'listing');
  const settings = tpl.settings;
  const type = slug ? sf.shop.bySlug[slug] || null : null;
  const f = useMemo(() => {
    const parsed = parseFilters(params, type);
    return { ...parsed, sort: effectiveSort(params.get('sort') ? parsed.sort : '', settings.default_sort, SORTS) };
  }, [params, type, settings.default_sort]);
  const apply = (next) => setParams(withSortParam(filtersToParams(next), next.sort, settings.default_sort));
  const result = useCatalogSearch(f, type, { size: pageSizeOf(settings), paging: settings.paging, enabled: !slug || Boolean(type) });
  const ctx = useMemo(
    () => buildListing(sf, { type, f, settings, apply, openFilters: () => setSheet(true), result }),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [sf.shop, sf.doc, type, f, result.items, result.total, result.facets, result.loading, result.error]
  );

  if (slug && sf.shop.types.length && !type) {
    return <EmptyState title="Nothing here" action={<Button onClick={() => navigate('/shop')}>Browse everything</Button>}>This store does not have that section.</EmptyState>;
  }

  return (
    <ListingCtx.Provider value={ctx}>
      <div className="space-y-5">
        <Region blocks={tpl.blocks} slots={LISTING_SLOT_COMPONENTS} className="-mx-4 sm:-mx-6" />
      </div>
      <Sheet
        isOpen={sheet}
        onClose={() => setSheet(false)}
        placement={wide ? 'left' : 'bottom'}
        title="Filters"
      >
        <div className="space-y-4 pb-2">
          {ctx.panel}
          <div className="sticky bottom-0 bg-[var(--app-surface)] pt-2">
            <Button fullWidth onClick={() => setSheet(false)}>Show {ctx.total} result{ctx.total === 1 ? '' : 's'}</Button>
          </div>
        </div>
      </Sheet>
    </ListingCtx.Provider>
  );
}
